import { useDispatch, useSelector } from 'react-redux'
import { useCallback } from 'react'
import _ from 'lodash'

import {
  Notification,
  NotificationType,
  AccountNotificationsActions,
  getUnreadNotificationsCount,
  getAccountNotifications
} from './redux/account/notifications'
import { useReduxEvent, useReduxEvents } from './utils/eventr'
import { useTablePagination } from './redux/table/hooks'
import { AccountReportsActions } from './redux/account/reports'
import { OrdersActions } from './redux/orders'
import { DialogActions } from './redux/dialog'
import { ImportValidationActions } from './redux/import/import-validation'
import { ImportHistoryActions } from './redux/account/imports'
import { getMaximumNumberOfSupportedItems } from './redux/config'

export const GlobalEventHandler = () => {
  const dispatch = useDispatch()
  const { pagination } = useTablePagination('archiveNotifications')
  const unreadNotificationCount = useSelector(getUnreadNotificationsCount)
  const notifications = useSelector(getAccountNotifications)
  const maxOrderItemsSupported = useSelector(getMaximumNumberOfSupportedItems)

  useReduxEvent(() => {
    openArchiveExportDialog(true)
  }, NotificationType.ReportsExportFailed)

  useReduxEvent(() => {
    openTransactionsExportDialog(true)
  }, NotificationType.OrdersExportFailed)

  useReduxEvent(() => {
    openValidationResultsExportDialog(true)
  }, NotificationType.ImportItemsExportFailed)

  useReduxEvent(() => {
    openImportsExportDialog(true)
  }, NotificationType.ImportsExportFailed)

  useReduxEvents(
    event => {
      dispatch(
        AccountNotificationsActions.setNotificationsStats({
          unreadCount: unreadNotificationCount + 1
        })
      )
      if (pagination.current === 1) {
        const newNotification = { type: event.type, ...event.data } as Notification
        const newNotifications = [newNotification, ...notifications].slice(0, pagination.pageSize)
        const sortedNotifications = _.orderBy(newNotifications, n => new Date(n.createdAt), ['desc'])
        dispatch(
          AccountNotificationsActions.setNotifications({
            totalPages: pagination.total,
            page: pagination.current,
            data: sortedNotifications
          })
        )
      }
    },
    [
      NotificationType.OrderSuccessfullyPlaced,
      NotificationType.OrderPartiallyPlaced,
      NotificationType.ErrorDuringOrderProcessing,
      NotificationType.ReportIsDelivered,
      NotificationType.CancelledReportRequest,
      NotificationType.ErrorDuringReportDownload,
      NotificationType.CustomerRequestPriceChanged,
      NotificationType.ImportValidationFinalized,
      NotificationType.ImportSearchFinalized,
      NotificationType.ImportCheckProductsFinalized,
      NotificationType.ImportCartCreated,
      NotificationType.ImportActionError
    ]
  )

  useReduxEvent(event => {
    const { exportId } = event.data
    dispatch(DialogActions.closeDialog())
    dispatch(
      AccountReportsActions.downloadExport({
        exportId
      })
    )
  }, NotificationType.ReportsExportCompleted)

  useReduxEvent(event => {
    const { exportId } = event.data
    dispatch(DialogActions.closeDialog())
    dispatch(
      OrdersActions.downloadExport({
        exportId
      })
    )
  }, NotificationType.OrdersExportCompleted)

  useReduxEvent(event => {
    const { exportId } = event.data
    dispatch(DialogActions.closeDialog())
    dispatch(
      ImportValidationActions.downloadExport({
        exportId
      })
    )
  }, NotificationType.ImportItemsExportCompleted)

  useReduxEvent(event => {
    const { exportId } = event.data
    dispatch(DialogActions.closeDialog())
    dispatch(
      ImportHistoryActions.downloadExport({
        exportId
      })
    )
  }, NotificationType.ImportsExportCompleted)

  useReduxEvent(() => {
    openImportCartItemsExceededDialog()
  }, NotificationType.ImportCartItemsExceeded)

  const openArchiveExportDialog = useCallback(
    (isError?: boolean) => {
      dispatch(
        DialogActions.showDialogWithData({
          dialog: 'ArchiveExportDialog',
          data: {
            isError: isError
          }
        })
      )
    },
    [dispatch]
  )

  const openTransactionsExportDialog = useCallback(
    (isError?: boolean) => {
      dispatch(
        DialogActions.showDialogWithData({
          dialog: 'TransactionsExport',
          data: {
            isError: isError
          }
        })
      )
    },
    [dispatch]
  )

  const openValidationResultsExportDialog = useCallback(
    (isError?: boolean) => {
      dispatch(
        DialogActions.showDialogWithData({
          dialog: 'ImportValidationExport',
          data: {
            isError: isError
          }
        })
      )
    },
    [dispatch]
  )

  const openImportsExportDialog = useCallback(
    (isError?: boolean) => {
      dispatch(
        DialogActions.showDialogWithData({
          dialog: 'ImportHistoryExport',
          data: {
            isError: isError
          }
        })
      )
    },
    [dispatch]
  )

  const openImportCartItemsExceededDialog = useCallback(() => {
    dispatch(
      DialogActions.showInfoDialog({
        i18nText: 'cart:MaximumItemsSupportdExceededText',
        i18nTitle: 'cart:MaximumItemsSupportExceededTitle',
        namespaces: ['cart'],
        textParam: { maximumRecords: maxOrderItemsSupported }
      })
    )
  }, [dispatch, maxOrderItemsSupported])

  return <></>
}
